// FICHA DE PROSPECTO (F4, Karla 30-sep-2026, videos 15:17–15:23): «no está bien definido cómo debe ser un
// perfil de prospecto y uno de alumno». Un prospecto es solo nombre, teléfono y lo que ha venido a probar:
// esta tarjeta enseña eso (clases sueltas pagadas, lista de espera) y el botón «Convertir en alumno», que
// pasa el rol a alumno y lleva a Clases para inscribirlo. La ficha completa sigue a un clic.
// También sale en el CONFLICTO (inscrito pero marcado prospecto): ahí el botón arregla el rol.
import { useState } from 'react';
import { UserCheck, Ticket, Hourglass, AlertTriangle, ChevronDown } from 'lucide-react';
import { api } from '../../../services/apiClient';
import { useToast } from '../../../context/ToastContext';

const TIPO: Record<string, string> = {
  prospecto: 'Prospecto', visitante: 'Prospecto · ya vino a clase suelta', ex_alumna: 'Ex-alumna (hoy prospecto)',
};
const money = (n: number) => `$${Number(n || 0).toLocaleString('es-MX')}`;
const fecha = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(`${iso}T12:00:00`);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
};

export function convertirEnAlumno(id: number) {
  return api.post<{ ok: true; converted: boolean }>(`/members/${id}/convertir`, {});
}

export default function FichaProspecto({ m, puedeConvertir, verCompleta, onVerCompleta, onConvertido }: {
  m: any; puedeConvertir: boolean; verCompleta: boolean;
  onVerCompleta: () => void; onConvertido: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const conflicto = !m.es_prospecto && !!m.prospecto_conflicto;
  const visitas: any[] = m.visitas || [];
  const espera: any[] = m.lista_espera || [];

  async function convertir() {
    const nombre = `${m.firstname} ${m.lastname || ''}`.trim();
    if (!conflicto && !confirm(`¿Convertir a ${nombre} en alumno?\n\nSale de Prospectos y pasa a Alumnos. Después completa sus datos e inscríbelo en su clase.`)) return;
    setBusy(true);
    try {
      await convertirEnAlumno(m.account_id);
      toast.success(conflicto ? `${nombre} ya quedó como alumno` : `${nombre} ahora es alumno · inscríbelo en su clase`);
      onConvertido();
    } catch (e: any) { toast.error(e?.message || 'No se pudo convertir'); }
    finally { setBusy(false); }
  }

  if (conflicto) {
    return (
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-500/40 bg-amber-500/5 p-4" data-testid="ficha-conflicto">
        <p className="flex items-start gap-2 text-sm text-content">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-500" />
          Está inscrito en una clase pero sigue marcado como prospecto, así que no se le generan mensualidades.
        </p>
        {puedeConvertir && (
          <button onClick={convertir} disabled={busy} data-testid="btn-convertir"
            className="flex items-center gap-1.5 rounded-lg bg-accent-solid px-3 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90 disabled:bg-line">
            <UserCheck size={15} /> {busy ? 'Guardando…' : 'Marcar como alumno'}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-2xl bg-surface p-4 shadow-sm" data-testid="ficha-prospecto">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-sky-600 dark:text-sky-400">{TIPO[m.persona_tipo] || 'Prospecto'}</div>
          <p className="mt-0.5 text-sm text-content-2">
            {visitas.length ? `${visitas.length} clase${visitas.length === 1 ? '' : 's'} suelta${visitas.length === 1 ? '' : 's'} / muestra` : 'Aún no ha tomado clase'}
            {m.ultima_visita && <> · última asistencia {fecha(m.ultima_visita)}</>}
          </p>
        </div>
        {puedeConvertir && (
          <button onClick={convertir} disabled={busy} data-testid="btn-convertir"
            className="flex items-center gap-1.5 rounded-lg bg-accent-solid px-4 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90 disabled:bg-line">
            <UserCheck size={16} /> {busy ? 'Guardando…' : 'Convertir en alumno'}
          </button>
        )}
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div>
          <h4 className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-content-3"><Ticket size={13} /> Clases sueltas pagadas</h4>
          {visitas.length ? (
            <ul className="space-y-1 text-sm">
              {visitas.map((v) => (
                <li key={v.payment_id} className="flex items-center justify-between gap-2 rounded-lg bg-surface-2 px-2.5 py-1.5">
                  <span className="min-w-0 truncate text-content">{v.clase || 'Clase'} <span className="text-content-3">· {fecha(v.pay_date)}</span></span>
                  <span className="shrink-0 text-xs text-content-2">{Number(v.amount) > 0 ? money(v.amount) : 'muestra gratis'}</span>
                </li>
              ))}
            </ul>
          ) : <p className="text-sm text-content-3">Ninguna todavía. Se cobra en Caja → «Clase suelta».</p>}
        </div>
        <div>
          <h4 className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-content-3"><Hourglass size={13} /> Lista de espera</h4>
          {espera.length ? (
            <ul className="space-y-1 text-sm">
              {espera.map((w) => (
                <li key={w.class_id} className="rounded-lg bg-surface-2 px-2.5 py-1.5 text-content">{w.name} <span className="text-content-3">· desde {fecha(w.desde)}</span></li>
              ))}
            </ul>
          ) : <p className="text-sm text-content-3">No está esperando lugar en ninguna clase.</p>}
        </div>
      </div>

      {!verCompleta && (
        <button onClick={onVerCompleta} data-testid="btn-ficha-completa"
          className="mt-3 flex items-center gap-1 text-xs text-content-3 underline-offset-2 hover:text-content hover:underline">
          <ChevronDown size={13} /> Ver ficha completa (datos, pagos, asistencia…)
        </button>
      )}
    </div>
  );
}
